<template>
	<div v-if="mounted">
		<h3 v-if="withTitle">Wird eine bestimmte Ausbildung benötigt?</h3>
		<RadioGroup
			v-model="yesNo"
			:options="yesNoOptions"
			@input="emitChangeEvent"
		/>
		<div v-if="yesNo" class="sections mt-4">
			<section>
				<h3>Bitte wähle die Art der Ausbildung</h3>
				<SelectInline
					v-if="editSchoolType"
					v-model="schoolType"
					:characteristic-items="meta.schoolType"
					initial-show-all
					single-choice
					:label-type="labelTypeJobProfile"
					:description-config="{ inline: true, html: true }"
					@input="setSchoolType"
				/>
				<div v-else>
					<Label :characteristic="schoolTypeCharacteristic" :label-type="labelTypeJobProfile"/>
					<a href="#" class="ml-1" @click.prevent="editSchoolType = true">
						<Icon icon="edit" color="primary"/>
					</a>
				</div>
			</section>
			<section v-if="professionEligible">
				<h3>Muss die Ausbildung bestimmte fachliche Schwerpunkte haben?</h3>
				<RadioGroup
					v-model="professionsYesNo"
					:options="yesNoOptions"
					@input="professionsYesNoChanged"
				/>
				<template v-if="professionsYesNo">
					<CharacteristicEdit
						v-model="professions"
						ref="professions"
						title="Fachliche Schwerpunkte der Ausbildung"
						:with-keyword="true"
						:characteristic-type="meta.schoolProfessionType"
						:characteristic-items="schoolProfessions"
						show-multiple-hint
						open-fan-button-alignment="left"
						open-fan-label="Fachliche Schwerpunkte auswählen"
						open-fan-label-with-selection="Fachliche Schwerpunkte bearbeiten"
						:label-type="labelTypeJobProfile"
						class="mt-3"
						@input="emitChangeEvent"
					/>
				</template>
			</section>
		</div>
	</div>
</template>

<script>
import {mapState} from "vuex";
import RadioGroup from "@/vue/common/form/RadioGroup.vue";
import {isDefined} from "@/helper/Value";
import SchoolTypeSingleSelect from "@/vue/characteristic/SingleSelect";
import * as CharacteristicTag from "@/helper/characteristic/Tag";
import CharacteristicEdit from "@/vue/characteristic/Edit"
import {JOB_PROFILE} from "@/helper/characteristic/Label";
import SelectInline from "@/vue/characteristic/SelectInline.vue";
import _ from "lodash";
import {byId} from "@/helper/application/characteristic/Provider";
import Label from "@/vue/characteristic/Label.vue";
import Icon from "@/vue/icon/Icon.vue";
import {cleanUp, removeItemsWithCorrelation, TYPE__SUPPRESS_FOR_SCHOOL_TYPE} from "@/helper/characteristic/Correlation";

export default {

	components: {
		Icon,
		Label,
		SelectInline,
		RadioGroup,
		SchoolTypeSingleSelect,
		CharacteristicEdit
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		withTitle: {
			type: Boolean,
			required: false,
			default: false
		}
	},

	mounted ()
	{
		if (this.value && this.value.schoolType)
		{
			this.schoolType = [{
				item: byId(this.value.schoolType.id, this.meta.schoolType)
			}];
		}

		if (this.value && !isDefined(this.professions) && this.professionEligible)
		{
			this.professions = [];
		}

		this.mounted = true;

		this.emitChangeEvent();
	},

	data ()
	{
		let yesNo = undefined;
		let professionsYesNo = undefined;

		if (this.value === null)
		{
			yesNo = false;
		}
		else if (isDefined(this.value))
		{
			yesNo = true;
			professionsYesNo = isDefined(this.value.professions);
		}

		return {
			yesNo,
			editSchoolType: !this.value?.schoolType,
			schoolType: undefined,
			professionsYesNo,
			professions: this.value?.professions,
			mounted: false
		}
	},

	computed: {
		...mapState('meta', ['meta']),
		labelTypeJobProfile ()
		{
			return JOB_PROFILE;
		},
		yesNoOptions ()
		{
			return [
				{value: true, text: 'Ja'},
				{value: false, text: 'Nein'},
			];
		},
		schoolTypeCharacteristic ()
		{
			return this.schoolType && this.schoolType.length === 1
				? _.first(this.schoolType).item
				: undefined;
		},
		schoolProfessions ()
		{
			const allItems = _.cloneDeep(this.meta.schoolProfession);

			if (!this.schoolTypeCharacteristic)
			{
				return allItems;
			}

			return removeItemsWithCorrelation({
				items: allItems,
				target: this.schoolTypeCharacteristic,
				type: TYPE__SUPPRESS_FOR_SCHOOL_TYPE
			});
		},
		professionEligible ()
		{
			return this.schoolTypeCharacteristic
				&& CharacteristicTag.hasTag(this.schoolTypeCharacteristic, CharacteristicTag.CAREER_EDUCATION_PROFESSION_ELIGIBLE);
		},
		professionsValid ()
		{
			if (!this.professionEligible)
			{
				return true;
			}

			if (!isDefined(this.professionsYesNo))
			{
				return false;
			}

			return this.professionsYesNo
				? isDefined(this.professions)
				: true;
		},
		valid ()
		{
			if (this.yesNo === false)
			{
				return true;
			}

			if (!isDefined(this.schoolTypeCharacteristic))
			{
				return false;
			}

			return this.professionsValid;
		},
	},

	methods: {
		setSchoolType (value)
		{
			this.schoolType = value;
			this.editSchoolType = false;

			this.professions = cleanUp({
				selected: this.professions,
				characteristicItems: this.schoolProfessions
			});

			const professionsEdit = this.$refs.professions;

			if (professionsEdit)
			{
				professionsEdit.changeSelected(this.professions);
			}

			this.emitChangeEvent();
		},
		professionsYesNoChanged (value)
		{
			this.professionsYesNo = value;

			if (value)
			{
				this.$nextTick(() => this.$refs.professions.openFan());
			}

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			if (this.yesNo === false)
			{
				this.$emit('input', null);
				return;
			}

			this.$emit('input', {
				schoolType: this.schoolTypeCharacteristic,
				professions: this.professionEligible && this.professionsYesNo
					? this.professions
					: undefined
			});
		},
	}
}
</script>
